import React, { useState, useEffect } from "react";
import { CandidateSearchFilters as FilterTypes } from "../types";

interface CandidateSearchFiltersProps {
  filters: FilterTypes;
  onFiltersChange: (filters: FilterTypes) => void;
  onSearch: (filters?: FilterTypes) => void;
  // onSearch: () => void;
  isLoading?: boolean;
}

const educationLevelLabels: Record<string, string> = {
  high_school: "High School",
  associate: "Associate Degree",
  bachelor: "Bachelor's Degree",
  master: "Master's Degree",
  phd: "PhD",
  other: "Other",
};

const remotePreferenceLabels: Record<string, string> = {
  remote_only: "Remote Only",
  hybrid: "Hybrid",
  on_site: "On-site",
  flexible: "Flexible",
};

const availabilityLabels: Record<string, string> = {
  immediately: "Immediately",
  "2_weeks": "Within 2 weeks",
  "1_month": "Within 1 month",
  "3_months": "Within 3 months",
  not_looking: "Not actively looking",
};

const formatFilterLabel = (
  labels: Record<string, string>,
  value?: string,
) => (value ? labels[value] || value.replace(/_/g, " ") : "");

const formatSalary = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const formatSalaryRange = (min?: number, max?: number) => {
  if (min !== undefined && max !== undefined) {
    return `${formatSalary(min)} - ${formatSalary(max)}`;
  }

  if (min !== undefined) {
    return `${formatSalary(min)}+`;
  }

  if (max !== undefined) {
    return `Up to ${formatSalary(max)}`;
  }

  return "";
};

const CandidateSearchFilters: React.FC<CandidateSearchFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState<FilterTypes>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof FilterTypes, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilterAndSearch = (updates: Partial<FilterTypes>) => {
    const newFilters = { ...localFilters, ...updates };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
    onSearch(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters: FilterTypes = {
      search: "",
      skills: "",
      location: "",
      job_title: "",
      experience_min: undefined,
      experience_max: undefined,
      salary_min: undefined,
      salary_max: undefined,
      education_level: "",
      employment_type: "",
      remote_preference: "",
      availability: "",
      languages: [],
      company: "",
      is_identity_verified: false,
    };

    setLocalFilters(emptyFilters);
    onFiltersChange(emptyFilters);

    onSearch(emptyFilters);
  };

  const hasActiveFilters = () => {
    return (
      localFilters.search ||
      localFilters.skills ||
      localFilters.location ||
      localFilters.job_title ||
      localFilters.experience_min ||
      localFilters.experience_max ||
      localFilters.salary_min ||
      localFilters.salary_max ||
      localFilters.education_level ||
      localFilters.employment_type ||
      localFilters.remote_preference ||
      localFilters.availability ||
      localFilters.languages?.length ||
      localFilters.company ||
      localFilters.is_identity_verified
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search candidates by name, skills, experience..."
                value={localFilters.search || ""}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <button
            onClick={() => onSearch(localFilters)}
            disabled={isLoading}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Searching...
              </div>
            ) : (
              "Search"
            )}
          </button>

          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <div className="flex items-center">
                <svg
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Clear
              </div>
            </button>
          )}

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <div className="flex items-center">
              <svg
                className="h-4 w-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                />
              </svg>
              Filters
              {hasActiveFilters() && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Active
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      {isExpanded && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Skills
                <span className="text-xs text-gray-500 ml-1">
                  (comma separated)
                </span>
              </label>
              <input
                type="text"
                placeholder="React, Python, AWS..."
                value={localFilters.skills || ""}
                onChange={(e) => handleFilterChange("skills", e.target.value)}
                autoComplete="off"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
                <span className="text-xs text-gray-500 ml-1">
                  (city, state, or country)
                </span>
              </label>
              <input
                type="text"
                placeholder="Irving, Texas, USA, San Francisco..."
                value={localFilters.location || ""}
                onChange={(e) => handleFilterChange("location", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Job Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title
                <span className="text-xs text-gray-500 ml-1">
                  (current or desired)
                </span>
              </label>
              <input
                type="text"
                placeholder="Software Engineer, Product Manager..."
                value={localFilters.job_title || ""}
                onChange={(e) =>
                  handleFilterChange("job_title", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current/Previous Company
                <span className="text-xs text-gray-500 ml-1">
                  (comma separated)
                </span>
              </label>
              <input
                type="text"
                placeholder="Google, Microsoft, Sify, Startup..."
                value={localFilters.company || ""}
                onChange={(e) => handleFilterChange("company", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Experience Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Experience (Years)
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  min="0"
                  max="50"
                  value={localFilters.experience_min || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "experience_min",
                      e.target.value ? parseInt(e.target.value) : undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  min="0"
                  max="50"
                  value={localFilters.experience_max || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "experience_max",
                      e.target.value ? parseInt(e.target.value) : undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Salary Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Salary Range ($)
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  min="0"
                  step="1000"
                  value={localFilters.salary_min || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "salary_min",
                      e.target.value ? parseInt(e.target.value) : undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <input
                  type="number"
                  placeholder="Max"
                  min="0"
                  step="1000"
                  value={localFilters.salary_max || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "salary_max",
                      e.target.value ? parseInt(e.target.value) : undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Education Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Education Level
              </label>
              <select
                value={localFilters.education_level || ""}
                onChange={(e) =>
                  handleFilterChange("education_level", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Any</option>
                <option value="high_school">High School</option>
                <option value="associate">Associate Degree</option>
                <option value="bachelor">Bachelor's Degree</option>
                <option value="master">Master's Degree</option>
                <option value="phd">PhD</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Employment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employment Type
              </label>
              <select
                value={localFilters.employment_type || ""}
                onChange={(e) =>
                  handleFilterChange("employment_type", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Any</option>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
                <option value="internship">Internship</option>
              </select>
            </div>

            {/* ID Verification */}
            <div className="flex items-center pt-6">
              <label className="inline-flex items-center text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={localFilters.is_identity_verified === true}
                  onChange={(e) =>
                    handleFilterChange("is_identity_verified", e.target.checked)
                  }
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2">Show only ID verified candidates</span>
              </label>
            </div>

            {/* Remote Preference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remote Work
              </label>
              <select
                value={localFilters.remote_preference || ""}
                onChange={(e) =>
                  handleFilterChange("remote_preference", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Any</option>
                <option value="remote_only">Remote Only</option>
                <option value="hybrid">Hybrid</option>
                <option value="on_site">On-site</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>

            {/* Availability */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Availability
              </label>
              <select
                value={localFilters.availability || ""}
                onChange={(e) =>
                  handleFilterChange("availability", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Any</option>
                <option value="immediately">Immediately</option>
                <option value="2_weeks">Within 2 weeks</option>
                <option value="1_month">Within 1 month</option>
                <option value="3_months">Within 3 months</option>
                <option value="not_looking">Not actively looking</option>
              </select>
            </div>

            {/* Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Languages
              </label>
              <input
                type="text"
                placeholder="English, Spanish, French (comma separated)"
                value={localFilters.languages?.join(", ") || ""}
                onChange={(e) => {
                  const languages = e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                  handleFilterChange("languages", languages);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear all filters
            </button>

            <div className="flex space-x-2">
              <button
                onClick={() => setIsExpanded(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Collapse
              </button>
              <button
                onClick={() => onSearch(localFilters)}
                disabled={isLoading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="p-4 bg-blue-50">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-blue-900">
              Active filters:
            </span>

            {localFilters.search && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{localFilters.search}"
                <button
                  onClick={() => clearFilterAndSearch({ search: "" })}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}

            {localFilters.skills && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Skills: {localFilters.skills}
                <button
                  onClick={() => clearFilterAndSearch({ skills: "" })}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}

            {localFilters.location && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Location: {localFilters.location}
                <button
                  onClick={() => clearFilterAndSearch({ location: "" })}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}

            {localFilters.job_title && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Job Title: {localFilters.job_title}
                <button
                  onClick={() => clearFilterAndSearch({ job_title: "" })}
                  className="ml-1 text-orange-600 hover:text-orange-800"
                >
                  ×
                </button>
              </span>
            )}

            {(localFilters.experience_min || localFilters.experience_max) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Experience: {localFilters.experience_min || 0}-
                {localFilters.experience_max || "∞"} years
                <button
                  onClick={() =>
                    clearFilterAndSearch({
                      experience_min: undefined,
                      experience_max: undefined,
                    })
                  }
                  className="ml-1 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            )}

            {localFilters.company && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                Current/Previous Company: {localFilters.company}
                <button
                  onClick={() => clearFilterAndSearch({ company: "" })}
                  className="ml-1 text-teal-600 hover:text-teal-800"
                >
                  x
                </button>
              </span>
            )}

            {(localFilters.salary_min !== undefined ||
              localFilters.salary_max !== undefined) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                Salary Range:{" "}
                {formatSalaryRange(
                  localFilters.salary_min,
                  localFilters.salary_max,
                )}
                <button
                  onClick={() =>
                    clearFilterAndSearch({
                      salary_min: undefined,
                      salary_max: undefined,
                    })
                  }
                  className="ml-1 text-emerald-600 hover:text-emerald-800"
                >
                  x
                </button>
              </span>
            )}

            {localFilters.education_level && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                Education Level:{" "}
                {formatFilterLabel(
                  educationLevelLabels,
                  localFilters.education_level,
                )}
                <button
                  onClick={() => clearFilterAndSearch({ education_level: "" })}
                  className="ml-1 text-cyan-600 hover:text-cyan-800"
                >
                  x
                </button>
              </span>
            )}

            {localFilters.employment_type && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                Work Type: {localFilters.employment_type.replace(/_/g, " ")}
                <button
                  onClick={() => clearFilterAndSearch({ employment_type: "" })}
                  className="ml-1 text-pink-600 hover:text-pink-800"
                >
                  &times;
                </button>
              </span>
            )}

            {localFilters.education_level && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                Education: {localFilters.education_level.replace(/_/g, " ")}
                <button
                  onClick={() => clearFilterAndSearch({ education_level: "" })}
                  className="ml-1 text-teal-600 hover:text-teal-800"
                >
                  &times;
                </button>
              </span>
            )}

            {localFilters.remote_preference && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                Remote Work: {localFilters.remote_preference.replace(/_/g, " ")}
                <button
                  onClick={() => clearFilterAndSearch({ remote_preference: "" })}
                  className="ml-1 text-sky-600 hover:text-sky-800"
                >
                  &times;
                </button>
              </span>
            )}

            {localFilters.availability && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                Availability: {localFilters.availability.replace(/_/g, " ")}
                <button
                  onClick={() => clearFilterAndSearch({ availability: "" })}
                  className="ml-1 text-cyan-600 hover:text-cyan-800"
                >
                  &times;
                </button>
              </span>
            )}

            {localFilters.languages && localFilters.languages.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                Languages: {localFilters.languages.join(", ")}
                <button
                  onClick={() => clearFilterAndSearch({ languages: [] })}
                  className="ml-1 text-violet-600 hover:text-violet-800"
                >
                  &times;
                </button>
              </span>
            )}

            {localFilters.remote_preference && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800">
                Remote Work:{" "}
                {formatFilterLabel(
                  remotePreferenceLabels,
                  localFilters.remote_preference,
                )}
                <button
                  onClick={() =>
                    clearFilterAndSearch({ remote_preference: "" })
                  }
                  className="ml-1 text-sky-600 hover:text-sky-800"
                >
                  x
                </button>
              </span>
            )}

            {localFilters.availability && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-lime-100 text-lime-800">
                Availability:{" "}
                {formatFilterLabel(
                  availabilityLabels,
                  localFilters.availability,
                )}
                <button
                  onClick={() => clearFilterAndSearch({ availability: "" })}
                  className="ml-1 text-lime-600 hover:text-lime-800"
                >
                  x
                </button>
              </span>
            )}

            {localFilters.languages && localFilters.languages.length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                Languages: {localFilters.languages.join(", ")}
                <button
                  onClick={() => clearFilterAndSearch({ languages: [] })}
                  className="ml-1 text-violet-600 hover:text-violet-800"
                >
                  x
                </button>
              </span>
            )}

            {localFilters.is_identity_verified && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                ID Verified
                <button
                  onClick={() =>
                    clearFilterAndSearch({ is_identity_verified: false })
                  }
                  className="ml-1 text-indigo-600 hover:text-indigo-800"
                >
                  &times;
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateSearchFilters;
